/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { identityProviderConnectionsReplaceClientSecret } from "../funcs/identityProviderConnectionsReplaceClientSecret.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { OktaIdentityProviderConnection } from "../models/components/oktaidentityproviderconnection.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ReplaceIdentityProviderConnectionClientSecretRequest,
  ReplaceIdentityProviderConnectionClientSecretSecurity,
} from "../models/operations/replaceidentityproviderconnectionclientsecret.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type ReplaceIdentityProviderConnectionClientSecretMutationVariables = {
  request: ReplaceIdentityProviderConnectionClientSecretRequest;
  security?: ReplaceIdentityProviderConnectionClientSecretSecurity | undefined;
  options?: RequestOptions;
};

export type ReplaceIdentityProviderConnectionClientSecretMutationData =
  OktaIdentityProviderConnection;

export type ReplaceIdentityProviderConnectionClientSecretMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * replaceClientSecret identityProviderConnections
 *
 * @remarks
 * Replace the client secret of a connection installed from the Okta Integration Network and re-verify it. The previous secret is kept if Okta rejects the new one. Requires org:admin.
 */
export function useReplaceIdentityProviderConnectionClientSecretMutation(
  options?: MutationHookOptions<
    ReplaceIdentityProviderConnectionClientSecretMutationData,
    ReplaceIdentityProviderConnectionClientSecretMutationError,
    ReplaceIdentityProviderConnectionClientSecretMutationVariables
  >,
): UseMutationResult<
  ReplaceIdentityProviderConnectionClientSecretMutationData,
  ReplaceIdentityProviderConnectionClientSecretMutationError,
  ReplaceIdentityProviderConnectionClientSecretMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildReplaceIdentityProviderConnectionClientSecretMutation(
      client,
      options,
    ),
    ...options,
  });
}

export function mutationKeyReplaceIdentityProviderConnectionClientSecret(): MutationKey {
  return ["@gram/client", "identityProviderConnections", "replaceClientSecret"];
}

export function buildReplaceIdentityProviderConnectionClientSecretMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: ReplaceIdentityProviderConnectionClientSecretMutationVariables,
  ) => Promise<ReplaceIdentityProviderConnectionClientSecretMutationData>;
} {
  return {
    mutationKey: mutationKeyReplaceIdentityProviderConnectionClientSecret(),
    mutationFn:
      function replaceIdentityProviderConnectionClientSecretMutationFn({
        request,
        security,
        options,
      }): Promise<ReplaceIdentityProviderConnectionClientSecretMutationData> {
        const mergedOptions = {
          ...hookOptions,
          ...options,
          fetchOptions: {
            ...hookOptions?.fetchOptions,
            ...options?.fetchOptions,
            signal: combineSignals(
              hookOptions?.fetchOptions?.signal,
              options?.fetchOptions?.signal,
            ),
          },
        };
        return unwrapAsync(identityProviderConnectionsReplaceClientSecret(
          client$,
          request,
          security,
          mergedOptions,
        ));
      },
  };
}
